import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './BlogPost.css';

// ─────────────────────────────────────────────
// BLOG POST DATA
// To add a new post: copy one of the objects below,
// update the slug, title, and sections, then add a
// matching <Link> in HomePage.js, a route in App.js,
// and an entry in src/seo/routeMeta.json so the post
// gets its own title/description in the prerendered
// HTML and in the sitemap.
// ─────────────────────────────────────────────

const blogPosts = [

  // ── POST 1 ───────────────────────────────────
  {
    slug: 'why-do-all-websites-look-the-same',
    title: 'In the Age of Templates, Trends, and Tech‑Trends: Why Custom Web Development Still Matters',
    metaDescription: 'A long‑form, SEO‑optimized article exploring why custom web development still matters in an age of templates and trends.',
    sections: [
      {
        type: 'intro',
        heading: 'A Digital World Filled With Sameness',
        paragraphs: [
          'In the age of templates, trends, and tech‑trends, the web is overflowing with websites that look the same, feel the same, and function the same. Pre‑made themes, drag‑and‑drop builders, and AI‑generated layouts have made it easier than ever to launch a site — but they\'ve also made it harder than ever to stand out.',
          'True web development is no longer about assembling components. It\'s about crafting digital experiences that feel intentional, human, and built to last.',
          'At Imanje Solutions, we believe a website should be more than a digital placeholder. It should be a living, evolving extension of your brand — built with purpose, clarity, and long‑term scalability.',
        ],
      },
      {
        type: 'numbered-sections',
        heading: 'Why Custom Web Development Still Wins',
        items: [
          {
            number: '1',
            title: 'Performance That Templates Can\'t Match',
            intro: 'Pre‑built themes often come bloated with unnecessary scripts, animations, and plugins. Custom development allows you to:',
            bullets: [
              'Load faster on every device',
              'Reduce code bloat',
              'Improve Core Web Vitals',
              'Increase conversion rates',
              'Strengthen SEO performance',
            ],
            closing: 'Speed isn\'t just a technical metric — it\'s a trust signal.',
          },
          {
            number: '2',
            title: 'Accessibility Built Into the Foundation',
            intro: 'Accessibility isn\'t optional anymore. It\'s a requirement for modern, ethical design. We build with:',
            bullets: [
              'Semantic HTML',
              'Proper ARIA roles',
              'Keyboard‑friendly navigation',
              'High‑contrast, readable typography',
              'Screen‑reader‑friendly structure',
            ],
            closing: 'When your site is accessible, you reach more people — and search engines notice.',
          },
          {
            number: '3',
            title: 'A Design That Reflects Your Brand, Not a Template',
            intro: 'Your brand has a voice, a personality, and a story. Templates don\'t. Custom development allows us to:',
            bullets: [
              'Craft layouts that match your brand identity',
              'Build interactions that feel intuitive and human',
              'Create emotional resonance through design',
              'Guide users toward meaningful actions',
            ],
            closing: 'Your website becomes a strategic asset, not a generic container.',
          },
          {
            number: '4',
            title: 'Scalability for the Future',
            intro: 'Your business will grow. Your website should grow with it. Custom architecture gives you:',
            bullets: [
              'Cleaner code',
              'Easier updates',
              'Flexible integrations',
              'Room for new features',
              'Long‑term stability',
            ],
            closing: 'You\'re not boxed in by the limitations of a theme or builder.',
          },
        ],
      },
      {
        type: 'subsections',
        heading: 'The Human Side of Code',
        items: [
          {
            title: 'We Start With Understanding',
            intro: 'Before we design or develop anything, we learn:',
            bullets: [
              'What your business stands for',
              'Who your audience is',
              'What problems your users face',
              'What goals matter most',
            ],
            closing: 'This ensures the final product isn\'t just functional — it\'s meaningful.',
          },
          {
            title: 'We Design With Emotion',
            intro: 'UI/UX isn\'t about making things pretty. It\'s about making people feel something:',
            bullets: ['Confidence', 'Curiosity', 'Trust', 'Momentum'],
            closing: 'When users feel understood, they stay longer, engage deeper, and convert more often.',
          },
          {
            title: 'We Develop With Integrity',
            bullets: [
              'We don\'t chase trends for the sake of trends.',
              'We don\'t use shortcuts that compromise long‑term stability.',
              'We don\'t rely on gimmicks to mask poor structure.',
            ],
            closing: 'We build websites that last.',
          },
        ],
      },
      {
        type: 'bullets-section',
        heading: 'SEO: The Silent Engine Behind Every Great Website',
        paragraphs: [
          'A beautiful website means nothing if no one can find it.',
          'That\'s why SEO is woven into our development process from day one.',
        ],
        subheading: 'Our SEO‑Driven Development Includes:',
        bullets: [
          'Clean, semantic HTML',
          'Fast‑loading assets',
          'Structured data',
          'Mobile‑first responsiveness',
          'Logical content hierarchy',
          'Ethical optimization practices',
        ],
        closing: 'Search engines reward clarity — and we build for clarity.',
      },
      {
        type: 'bullets-section',
        heading: 'Your Website Should Work as Hard as You Do',
        paragraphs: ['Your website is often the first impression someone has of your business. It should communicate:'],
        bullets: ['Professionalism', 'Reliability', 'Vision', 'Value'],
        closing: 'A custom‑built site does exactly that.',
      },
      {
        type: 'closing',
        cta: {
          heading: 'Let\'s Build Something That Lasts',
          body: 'In a world full of templates and trends, your brand deserves something timeless — something crafted with intention, strategy, and care. If you\'re ready to elevate your digital presence with a website that truly reflects your brand, we\'re here to build it with you.',
        },
      },
    ],
  },

  // ── POST 2 ───────────────────────────────────
  {
    slug: 'designing-for-different-cultures',
    title: 'Designing for Different Cultures: Challenges, Tips, and the Role of AI',
    metaDescription: 'In a globalized digital landscape, designing for diverse cultures isn\'t just a creative challenge — it\'s a strategic imperative.',
    sections: [
      {
        type: 'intro',
        heading: 'Designing for Different Cultures',
        paragraphs: [
          'In a globalized digital landscape, designing for diverse cultures isn\'t just a creative challenge — it\'s a strategic imperative. Every color, shape, font, and symbol carries cultural weight, and what feels intuitive in one region may be confusing — or even offensive — in another.',
          'As brands expand across borders, designers must learn to navigate this complex terrain with empathy, research, and increasingly, artificial intelligence.',
        ],
      },
      // ── Add more sections here when full content is ready ──
      // Follow the same pattern as Post 1:
      // { type: 'bullets-section', heading: '...', paragraphs: [...], bullets: [...], closing: '...' }
      // { type: 'subsections', heading: '...', items: [{ title, intro, bullets, closing }] }
      {
        type: 'closing',
        cta: {
          heading: 'Ready to Build for a Global Audience?',
          body: 'At Imanje Solutions, we design with cultural intelligence. Let\'s create something that resonates across borders.',
        },
      },
    ],
  },

  // ── POST 3 ───────────────────────────────────
  {
    slug: '5-skills-ai-cant-replace',
    title: '5 Skills AI Can\'t Replace: How To Double Down on Them',
    metaDescription: 'Discover the five human skills AI can\'t replace — and how to strengthen them to stay relevant in an AI‑driven world.',
    sections: [
      {
        type: 'intro',
        heading: 'Introduction',
        paragraphs: [
          'In a world where artificial intelligence is rapidly evolving, certain human skills remain irreplaceable. AI can automate workflows, generate content, and analyze data at a scale no human could match — but it still can\'t replicate the depth of human intuition, emotional intelligence, or the creative leaps that define meaningful work.',
          'The future won\'t belong to those who try to compete against AI. It will belong to those who strengthen the skills AI can\'t imitate — and use AI as a tool to amplify them.',
          'Below are the five human skills AI can\'t replace, why they matter more than ever, and how you can double down on each one to stay relevant, resilient, and impossible to automate.',
        ],
      },
      {
        type: 'skill-sections',
        items: [
          {
            number: '1',
            title: 'Emotional Intelligence (EQ)',
            why: {
              paragraphs: [
                'AI can analyze sentiment, but it can\'t feel it.',
                'It can detect patterns in behavior, but it can\'t understand the emotional context behind them.',
                'Human relationships — whether in leadership, sales, design, or customer experience — are built on empathy, trust, and emotional nuance. These are uniquely human strengths.',
              ],
            },
            howToDouble: {
              bullets: [
                'Practice active listening',
                'Ask deeper questions instead of assuming',
                'Learn to read non‑verbal cues',
                'Build emotional awareness through journaling or coaching',
                'Prioritize empathy in communication',
              ],
            },
            howAISupports: 'Use AI tools to analyze feedback, identify sentiment trends, or surface customer pain points — then apply your emotional intelligence to interpret and act on them.',
          },
          {
            number: '2',
            title: 'Critical Thinking & Judgment',
            why: {
              paragraphs: [
                'AI can process information, but it can\'t determine what matters.',
                'It can generate answers, but it can\'t evaluate consequences, ethics, or long‑term implications.',
                'Critical thinking is the human ability to question assumptions, evaluate sources, identify bias, make ethical decisions, and understand context. AI lacks moral reasoning and cannot weigh values the way humans do.',
              ],
            },
            howToDouble: {
              bullets: [
                'Challenge your own assumptions',
                'Seek out opposing viewpoints',
                'Ask "What\'s the long‑term impact?"',
                'Break problems into smaller parts',
                'Practice structured decision‑making',
              ],
            },
            howAISupports: 'Use AI to gather information quickly — then apply your judgment to interpret, validate, and decide.',
          },
          {
            number: '3',
            title: 'Creativity & Original Thought',
            why: {
              paragraphs: [
                'AI can remix what already exists. Humans can imagine what doesn\'t exist yet.',
                'True creativity requires lived experience, emotion, intuition, cultural understanding, and the ability to break patterns — not just follow them.',
                'AI can generate variations. Humans generate breakthroughs.',
              ],
            },
            howToDouble: {
              bullets: [
                'Expose yourself to new environments, art, and ideas',
                'Keep a "creative swipe file"',
                'Brainstorm without constraints',
                'Collaborate with people outside your field',
                'Embrace play, curiosity, and experimentation',
              ],
            },
            howAISupports: 'Use AI for idea generation, mood boards, or rapid prototyping — then refine, elevate, and humanize the output.',
          },
          {
            number: '4',
            title: 'Complex Problem‑Solving',
            why: {
              paragraphs: [
                'AI excels at solving defined problems. Humans excel at solving undefined ones.',
                'Real‑world challenges are often messy, ambiguous, and interconnected. They require systems thinking, cross‑disciplinary knowledge, human insight, and the ability to navigate uncertainty.',
                'AI can\'t fully understand context, nuance, or the human factors that shape complex decisions.',
              ],
            },
            howToDouble: {
              bullets: [
                'Practice breaking big problems into smaller ones',
                'Learn frameworks like first‑principles thinking',
                'Study case studies in your industry',
                'Collaborate with diverse thinkers',
                'Embrace experimentation and iteration',
              ],
            },
            howAISupports: 'Use AI to simulate scenarios, analyze data, or test assumptions — then apply your human reasoning to choose the best path.',
          },
          {
            number: '5',
            title: 'Leadership & Human Connection',
            why: {
              paragraphs: [
                'Leadership is fundamentally human.',
                'It requires vision, courage, empathy, inspiration, and the ability to unite people around a shared purpose.',
                'AI can\'t motivate, mentor, or build culture. It can\'t navigate interpersonal conflict or inspire trust.',
              ],
            },
            howToDouble: {
              bullets: [
                'Develop self‑awareness',
                'Communicate with clarity and empathy',
                'Learn to give and receive feedback',
                'Build psychological safety in teams',
                'Lead with purpose, not pressure',
              ],
            },
            howAISupports: 'Use AI to automate administrative tasks so you can focus on coaching, strategy, and human connection.',
          },
        ],
      },
      {
        type: 'bullets-section',
        heading: 'The Future Belongs to the Human‑AI Partnership',
        paragraphs: [
          'AI isn\'t here to replace humans — it\'s here to replace tasks.',
          'The people who thrive will be those who:',
        ],
        bullets: [
          'Use AI as a tool',
          'Strengthen the skills AI can\'t replicate',
          'Lean into their humanity, not away from it',
        ],
        closing: 'The future of work is not AI vs. humans. It\'s humans who know how to use AI vs. humans who don\'t.',
      },
      {
        type: 'bullets-section',
        heading: 'Conclusion: Double Down on What Makes You Human',
        paragraphs: ['If you want to stay relevant in an AI‑driven world, invest in the skills that make you uniquely human:'],
        bullets: [
          'Emotional intelligence',
          'Critical thinking',
          'Creativity',
          'Complex problem‑solving',
          'Leadership',
        ],
        closing: 'These are the skills that shape culture, build trust, and drive innovation — and no algorithm can replace them.',
      },
      {
        type: 'closing',
        cta: {
          heading: 'Ready to Future‑Proof Your Business?',
          body: 'At Imanje Solutions, we build with the future in mind — technology that amplifies human potential. Let\'s talk about how we can help your business thrive in an AI‑driven world.',
        },
      },
    ],
  },
];

// ─────────────────────────────────────────────
// SECTION RENDERERS
// ─────────────────────────────────────────────

function IntroSection({ section }) {
  return (
    <section className="blog-section">
      <h2 className="blog-section-heading">{section.heading}</h2>
      {section.paragraphs.map((p, i) => <p key={i} className="blog-paragraph">{p}</p>)}
    </section>
  );
}

function NumberedSection({ section }) {
  return (
    <section className="blog-section">
      <h2 className="blog-section-heading">{section.heading}</h2>
      {section.items.map((item, i) => (
        <div key={i} className="blog-numbered-item">
          <h3 className="blog-numbered-title">
            <span className="blog-number">{item.number}.</span> {item.title}
          </h3>
          {item.intro && <p className="blog-paragraph">{item.intro}</p>}
          <ul className="blog-list">
            {item.bullets.map((b, j) => <li key={j}>{b}</li>)}
          </ul>
          {item.closing && <p className="blog-closing">{item.closing}</p>}
        </div>
      ))}
    </section>
  );
}

function SubsectionsSection({ section }) {
  return (
    <section className="blog-section">
      <h2 className="blog-section-heading">{section.heading}</h2>
      {section.items.map((item, i) => (
        <div key={i} className="blog-subsection">
          <h3 className="blog-subsection-title">{item.title}</h3>
          {item.intro && <p className="blog-paragraph">{item.intro}</p>}
          <ul className="blog-list">
            {item.bullets.map((b, j) => <li key={j}>{b}</li>)}
          </ul>
          {item.closing && <p className="blog-closing">{item.closing}</p>}
        </div>
      ))}
    </section>
  );
}

function BulletsSection({ section }) {
  return (
    <section className="blog-section">
      <h2 className="blog-section-heading">{section.heading}</h2>
      {section.paragraphs?.map((p, i) => <p key={i} className="blog-paragraph">{p}</p>)}
      {section.subheading && <h3 className="blog-subsection-title">{section.subheading}</h3>}
      {section.bullets && (
        <ul className="blog-list">
          {section.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {section.closing && <p className="blog-closing">{section.closing}</p>}
    </section>
  );
}

function SkillSections({ section }) {
  return (
    <>
      {section.items.map((skill, i) => (
        <section key={i} className="blog-section skill-section">
          <h2 className="blog-section-heading skill-heading">
            <span className="blog-number">{skill.number}.</span> {skill.title}
          </h2>
          <div className="blog-subsection">
            <h3 className="blog-subsection-title">Why AI Can't Replace It</h3>
            {skill.why.paragraphs.map((p, j) => <p key={j} className="blog-paragraph">{p}</p>)}
          </div>
          <div className="blog-subsection">
            <h3 className="blog-subsection-title">How To Double Down</h3>
            <ul className="blog-list">
              {skill.howToDouble.bullets.map((b, j) => <li key={j}>{b}</li>)}
            </ul>
          </div>
          <div className="blog-subsection ai-support">
            <h3 className="blog-subsection-title">How AI Can Support It</h3>
            <p className="blog-paragraph">{skill.howAISupports}</p>
          </div>
        </section>
      ))}
    </>
  );
}

function ClosingSection({ section }) {
  return (
    <section className="blog-section">
      {section.cta && (
        <div className="blog-cta">
          <h3 className="blog-cta-heading">{section.cta.heading}</h3>
          <p className="blog-paragraph">{section.cta.body}</p>
          <a href="tel:+14702612528">
            <button className="contact-button-vision">Let's Talk</button>
          </a>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────
// SECTION ROUTER
// ─────────────────────────────────────────────

function renderSection(section, i) {
  switch (section.type) {
    case 'intro':             return <IntroSection       key={i} section={section} />;
    case 'numbered-sections': return <NumberedSection    key={i} section={section} />;
    case 'subsections':       return <SubsectionsSection key={i} section={section} />;
    case 'bullets-section':   return <BulletsSection     key={i} section={section} />;
    case 'skill-sections':    return <SkillSections      key={i} section={section} />;
    case 'closing':           return <ClosingSection     key={i} section={section} />;
    default:                  return null;
  }
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="blog-not-found">
        <Helmet>
          <title>Post Not Found - Imanje Solutions</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h2>Post not found</h2>
        <Link to="/" className="blog-back-link">← Back to Home</Link>
      </div>
    );
  }

  return (
    <article className="blog-post-page">
      <Helmet>
        <title>{`${post.title} - Imanje Solutions`}</title>
        <meta name="description" content={post.metaDescription} />
      </Helmet>
      <div className="blog-post-header">
        <Link to="/" className="blog-back-link">← Back to Home</Link>
        <h1 className="blog-post-title">{post.title}</h1>
      </div>
      <div className="blog-post-body">
        {post.sections.map((section, i) => renderSection(section, i))}
      </div>
    </article>
  );
}

export default BlogPost;
